<template>
  <AuthSplitLayout
    :hero-title="$t('auth.layout.heroTitleRegister')"
    :hero-subtitle="$t('auth.layout.heroSubRegister')"
    :locale="$i18n.locale"
    @lang="switchLanguage"
  >
    <h1 class="auth-title">{{ $t('auth.registerWelcomeTitle') }}</h1>
    <form class="auth-form" @submit.prevent="register">
      <label class="auth-label">{{ $t('register.email') }}</label>
      <input
        v-model="email"
        type="email"
        autocomplete="email"
        :placeholder="$t('register.email')"
        required
      />
      <label class="auth-label">{{ $t('register.password') }}</label>
      <input
        v-model="password"
        type="password"
        autocomplete="new-password"
        :placeholder="$t('register.password')"
        required
      />
      <label class="auth-label">{{ $t('register.inviteCode') }}</label>
      <input
        v-model="inviteCode"
        type="text"
        autocomplete="off"
        :placeholder="$t('register.inviteCode')"
        required
      />
      <button type="submit" class="auth-btn auth-btn--primary">{{ $t('register.registerButton') }}</button>
    </form>
    <p class="auth-switch">
      {{ $t('auth.hasAccount') }}
      <router-link :to="loginLink">{{ $t('register.login') }}</router-link>
    </p>
    <div class="auth-divider">
      <span>{{ $t('auth.or') }}</span>
    </div>
    <div v-if="googleClientId" ref="googleMount" class="google-mount" />
    <p v-else class="auth-hint-muted">{{ $t('auth.googleUnavailable') }}</p>
    <p class="auth-invite-hint">{{ $t('register.inviteHintShort') }}</p>
  </AuthSplitLayout>
</template>

<script>
import AuthSplitLayout from '@/components/AuthSplitLayout.vue';
import { getGoogleClientId, mountGoogleSignInButton } from '@/utils/googleSignIn';
import { useAuthStore } from '@/stores/auth';
import { getSafeNextLocation } from '@/utils/postAuthRedirect';

export default {
  components: { AuthSplitLayout },
  computed: {
    loginLink() {
      const safe = getSafeNextLocation(this.$route.query.next);
      if (safe) return { path: "/login", query: { next: safe } };
      return "/login";
    },
  },
  data() {
    return {
      password: '',
      email: '',
      inviteCode: '',
      googleClientId: getGoogleClientId(),
    };
  },
  mounted() {
    const inviteFromLink = (this.$route.query.invite || '').toString().trim();
    if (inviteFromLink) {
      this.inviteCode = inviteFromLink;
    }
    this.$nextTick(() => this.setupGoogleButton());
  },
  methods: {
    async setupGoogleButton() {
      if (!this.googleClientId || !this.$refs.googleMount) return;
      try {
        await mountGoogleSignInButton(this.$refs.googleMount, {
          locale: this.$i18n.locale,
          onCredential: this.onGoogleCredential,
        });
      } catch (e) {
        console.warn('Google Sign-In button:', e);
      }
    },
    async onGoogleCredential(response) {
      const credential = response?.credential;
      if (!credential) return;
      if (!this.inviteCode.trim()) {
        alert(this.$t('register.inviteRequiredForGoogle'));
        return;
      }
      if (!window.confirm(this.$t('register.beforeRegisterInviteHint'))) {
        return;
      }
      try {
        const res = await fetch('/api/auth/google', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            credential,
            mode: 'register',
            invite_code: this.inviteCode.trim(),
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (res.ok && data.access_token) {
          const authStore = useAuthStore();
          authStore.login(data.access_token);
          const dest = getSafeNextLocation(this.$route.query.next);
          this.$router.push(dest || '/new/dashboard');
          return;
        }
        if (res.status === 503) {
          const parts503 = [data.hint, data.verify_message, data.message, data.error].filter(Boolean);
          alert(
            parts503.length ? parts503.join('\n\n') : this.$t('auth.googleServerDisabled'),
          );
          return;
        }
        if (res.status === 401 && data.verify_message) {
          console.error('[Google auth]', data.verify_message);
        }
        const parts = [data.hint, data.verify_message, data.message, data.error].filter(Boolean);
        alert(parts.length ? parts.join('\n\n') : this.$t('register.unknownError'));
      } catch (e) {
        console.error(e);
        alert(this.$t('register.error'));
      }
    },
    switchLanguage(lang) {
      this.$i18n.locale = lang;
      localStorage.setItem('language', lang);
      this.$nextTick(() => this.setupGoogleButton());
    },
    async register() {
      if (!window.confirm(this.$t('register.beforeRegisterInviteHint'))) {
        return;
      }
      try {
        const response = await fetch('/register', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: this.email,
            password: this.password,
            invite_code: this.inviteCode,
          }),
        });
        const data = await response.json();
        if (response.ok) {
          alert(this.$t('register.success'));
          const safe = getSafeNextLocation(this.$route.query.next);
          this.$router.push({ path: '/login', query: safe ? { next: safe } : {} });
        } else {
          alert(data.error || data.message || 'Registration failed');
        }
      } catch (error) {
        console.error(this.$t('register.error'), error);
      }
    },
  },
};
</script>

<style scoped>
.auth-title {
  margin: 0 0 24px;
  font-size: 26px;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: #111827;
}

.auth-form {
  text-align: left;
}

.auth-label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 6px;
}

.auth-form input {
  width: 100%;
  padding: 14px 16px;
  margin-bottom: 16px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  font-size: 15px;
  box-sizing: border-box;
}

.auth-form input:focus {
  outline: none;
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.15);
}

.auth-btn {
  width: 100%;
  padding: 14px 18px;
  font-size: 16px;
  font-weight: 600;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.2s;
}

.auth-btn--primary {
  background: #2563eb;
  color: #fff;
}

.auth-btn--primary:hover {
  background: #1d4ed8;
}

.auth-switch {
  margin-top: 20px;
  font-size: 14px;
  color: #6b7280;
  text-align: center;
}

.auth-switch a {
  color: #2563eb;
  font-weight: 600;
  text-decoration: none;
}

.auth-divider {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 24px 0;
  color: #9ca3af;
  font-size: 12px;
  font-weight: 600;
}

.auth-divider::before,
.auth-divider::after {
  content: '';
  flex: 1;
  height: 1px;
  background: #e5e7eb;
}

.google-mount {
  display: flex;
  justify-content: center;
  min-height: 44px;
}

.auth-hint-muted {
  font-size: 13px;
  color: #9ca3af;
  text-align: center;
}

.auth-invite-hint {
  margin-top: 16px;
  font-size: 13px;
  line-height: 1.45;
  color: #6b7280;
  text-align: center;
}
</style>
